import React, { useState, useRef, useEffect } from "react";
import { NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import {
  LayoutGrid,
  Boxes,
  ArrowLeftRight,
  PauseCircle,
  Wrench,
  ScrollText,
  LogOut,
  Package,
  ClipboardList,
  ShoppingBag,
  Repeat,
  Archive,
  ChevronDown,
  Settings,
  Database,
  UserCog,
  MonitorCog,
  FileText,
  HardDriveDownload,
  Users,
  Recycle,
  BarChart3, ClipboardCheck,
} from "lucide-react";
import { useAuth } from "../context/AuthContext";
import FeatureErrorBoundary, { BackendUnavailableNotice } from "./FeatureErrorBoundary";
import { isServerWaking, retryServerHealth } from "../utils/axiosInstance";
import { useViewMode } from "../context/ViewModeContext";
import flowpiLogo from "../assets/flowpi-logo.png";
import { desktopContentPadding, desktopRouteStructure, PAGE_STRUCTURE } from "../lib/pageStructureRoutes";

export function isQuotationFormRoute(pathname) {
  return pathname.startsWith("/quotations/") && desktopRouteStructure(pathname) === PAGE_STRUCTURE.PERSISTENT;
}

const NAV = [
  { to: "/inventory", label: "Inventory", icon: Boxes, testid: "nav-inventory", accent: "text-semantic-primary" },
  { to: "/transfer", label: "Transfer", icon: ArrowLeftRight, testid: "nav-transfer", accent: "text-semantic-primary" },
  { to: "/hold", label: "Hold", icon: PauseCircle, testid: "nav-hold", accent: "text-semantic-primary" },
  { to: "/usage", label: "Usage", icon: Wrench, testid: "nav-usage", accent: "text-semantic-primary" },
];

const SERVICE_NAV = [
  { to: "/service/sold", label: "Sold", icon: ShoppingBag, testid: "nav-service-sold", accent: "text-semantic-primary" },
  { to: "/service/rental", label: "Rental", icon: Repeat, testid: "nav-service-rental", accent: "text-semantic-primary" },
  { to: "/service/stock", label: "Stock", icon: Archive, testid: "nav-service-stock", accent: "text-semantic-primary" },
  { to: "/service/cannibal", label: "Cannibal", icon: Recycle, testid: "nav-service-cannibal", accent: "text-semantic-primary", roles: ["admin", "staff"] },
];

const MASTER_DATA_NAV = [
  { to: "/master-data/technicians", label: "Technicians", icon: UserCog, testid: "nav-master-data-technicians" },
  { to: "/master-data/device-models", label: "Device Models", icon: MonitorCog, testid: "nav-master-data-device-models" },
];

export default function Layout() {
  const { user, logout } = useAuth();
  const { setMode } = useViewMode();
  const location = useLocation();
  const navigate = useNavigate();
  const [profileOpen, setProfileOpen] = useState(false);
  const [sparePartOpen, setSparePartOpen] = useState(true);
  const [serviceOpen, setServiceOpen] = useState(true);
  const [masterDataOpen, setMasterDataOpen] = useState(true);
  const [backendUnavailable, setBackendUnavailable] = useState(isServerWaking);
  const [retryingBackend, setRetryingBackend] = useState(false);
  const [summaryAuditOpen, setSummaryAuditOpen] = useState(true);
  const profileRef = useRef(null);
  const isSparePartActive = NAV.some((n) =>
    n.to === "/" ? location.pathname === "/" : location.pathname.startsWith(n.to)
  );
  const isServiceActive = location.pathname === "/service" || location.pathname.startsWith("/service/");
  const contentPadding = desktopContentPadding(location.pathname);
  const isMasterDataActive = location.pathname.startsWith("/master-data/");
  const isQuotationActive = location.pathname === "/quotations" || location.pathname.startsWith("/quotations/");
  const isFirmwareActive = location.pathname === "/firmware";
  const isSummaryAuditActive = ["/summary/spare-part", "/report", "/audit", "/service/audit"].includes(location.pathname);
  const isManageUsersActive = location.pathname === "/manage-users";

  useEffect(() => {
    function handleClickOutside(e) {
      if (profileRef.current && !profileRef.current.contains(e.target)) {
        setProfileOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    const handleServerWaking = () => setBackendUnavailable(true);
    const handleServerReady = () => setBackendUnavailable(false);
    window.addEventListener("server-waking", handleServerWaking);
    window.addEventListener("server-ready", handleServerReady);
    return () => {
      window.removeEventListener("server-waking", handleServerWaking);
      window.removeEventListener("server-ready", handleServerReady);
    };
  }, []);

  async function handleBackendRetry() {
    setRetryingBackend(true);
    try {
      await retryServerHealth();
    } finally {
      setRetryingBackend(false);
    }
  }

  return (
    <div className="h-screen overflow-hidden bg-background" data-testid="desktop-shell">
      {/* Sidebar */}
      <aside
        className="fixed inset-y-0 left-0 z-40 w-[248px] bg-card border-r border-zinc-200 flex flex-col"
        data-testid="sidebar"
      >
        <div className="flex h-20 items-center justify-center px-4 border-b border-zinc-200">
          <img
            src={flowpiLogo}
            alt="Flowpi"
            className="h-auto w-full max-w-[200px] object-contain"
          />
        </div>

        <nav className="flex-1 overflow-y-auto py-4" data-testid="sidebar-nav">
          <NavLink to="/" end data-testid="nav-dashboard" className={({ isActive }) => `side-link ${isActive ? "parent-active" : ""}`}>
            <LayoutGrid className="w-4 h-4" style={{ color: "var(--service-jakarta-selected-accent)" }} strokeWidth={2} />
            Dashboard
          </NavLink>
          <button
            type="button"
            onClick={() => setSparePartOpen((open) => !open)}
            aria-expanded={sparePartOpen}
            data-testid="nav-spare-part-toggle"
            className={`side-link w-full ${isSparePartActive ? "parent-active" : ""}`}
          >
            <Package className="w-4 h-4" style={{ color: "var(--service-jakarta-selected-accent)" }} strokeWidth={2} />
            <span className="flex-1 text-left">Spare Part</span>
            <ChevronDown
              className={`w-3.5 h-3.5 transition-transform ${sparePartOpen ? "rotate-180" : ""}`}
              style={{ color: "var(--service-jakarta-selected-accent)" }}
              strokeWidth={2}
            />
          </button>

          {sparePartOpen && (
            <div
              className="py-1 ml-5 border-l border-zinc-200"
              data-testid="nav-spare-part-submenu"
            >
              {NAV.map((n) => (
                <NavLink
                  key={n.to}
                  to={n.to}
                  end={n.to === "/"}
                  data-testid={n.testid}
                  className={({ isActive }) => `side-link pl-6 pr-4 text-xs ${isActive ? "active" : ""}`}
                >
                  {({ isActive }) => (
                    <>
                      <n.icon className={`w-3.5 h-3.5 ${isActive ? "text-semantic-primary" : n.accent}`} strokeWidth={2} />
                      {n.label}
                    </>
                  )}
                </NavLink>
              ))}
            </div>
          )}

          <button
            type="button"
            onClick={() => setServiceOpen((open) => !open)}
            aria-expanded={serviceOpen}
            data-testid="nav-service-toggle"
            className={`side-link w-full ${isServiceActive ? "parent-active" : ""}`}
          >
            <ClipboardList className="w-4 h-4" style={{ color: "var(--service-jakarta-selected-accent)" }} strokeWidth={2} />
            <span className="flex-1 text-left">Service</span>
            <ChevronDown
              className={`w-3.5 h-3.5 transition-transform ${serviceOpen ? "rotate-180" : ""}`}
              style={{ color: "var(--service-jakarta-selected-accent)" }}
              strokeWidth={2}
            />
          </button>

          {serviceOpen && (
            <div
              className="py-1 ml-5 border-l border-zinc-200"
              data-testid="nav-service-submenu"
            >
              {SERVICE_NAV.filter((n) => !n.roles || n.roles.includes(user?.role)).map((n) => (
                <NavLink
                  key={n.to}
                  to={n.to}
                  data-testid={n.testid}
                  className={({ isActive }) => `side-link pl-6 pr-4 text-xs ${isActive ? "active" : ""}`}
                >
                  {({ isActive }) => (
                    <>
                      <n.icon className={`w-3.5 h-3.5 ${isActive ? "text-semantic-primary" : n.accent}`} strokeWidth={2} />
                      {n.label}
                    </>
                  )}
                </NavLink>
              ))}
            </div>
          )}

          {["staff", "admin"].includes(user?.role) ? (
            <NavLink
              to="/quotations"
              data-testid="nav-quotation"
              className={`side-link ${isQuotationActive ? "parent-active" : ""}`}
            >
              <FileText className="h-4 w-4" style={{ color: "var(--service-jakarta-selected-accent)" }} strokeWidth={2} />
              Quotation
            </NavLink>
          ) : null}

          {["staff", "admin"].includes(user?.role) ? (
            <NavLink
              to="/firmware"
              data-testid="nav-firmware"
              className={`side-link ${isFirmwareActive ? "parent-active" : ""}`}
            >
              <HardDriveDownload className="h-4 w-4" style={{ color: "var(--service-jakarta-selected-accent)" }} strokeWidth={2} />
              Firmware
            </NavLink>
          ) : null}

          <button type="button" onClick={() => setSummaryAuditOpen((open) => !open)} aria-expanded={summaryAuditOpen} data-testid="nav-summary-audit-toggle" className={`side-link w-full ${isSummaryAuditActive ? "parent-active" : ""}`}>
            <ClipboardCheck className="w-4 h-4" style={{ color: "var(--service-jakarta-selected-accent)" }} strokeWidth={2} />
            <span className="flex-1 text-left">Summary &amp; Audit</span><ChevronDown className={`w-3.5 h-3.5 transition-transform ${summaryAuditOpen ? "rotate-180" : ""}`} style={{ color: "var(--service-jakarta-selected-accent)" }} strokeWidth={2} />
          </button>
          {summaryAuditOpen ? <div className="py-1 ml-5 border-l border-zinc-200" data-testid="nav-summary-audit-submenu">
            {["staff", "admin"].includes(user?.role) ? <NavLink to="/summary/spare-part" data-testid="nav-spare-part-summary" className={({ isActive }) => `side-link pl-6 pr-4 text-xs ${isActive ? "active" : ""}`}><BarChart3 className="w-3.5 h-3.5 text-semantic-primary" />Spare Part Summary</NavLink> : null}
            {["staff", "admin"].includes(user?.role) ? <NavLink to="/report" data-testid="nav-report" className={({ isActive }) => `side-link pl-6 pr-4 text-xs ${isActive ? "active" : ""}`}><BarChart3 className="w-3.5 h-3.5 text-semantic-primary" />Service Summary</NavLink> : null}
            <NavLink to="/audit" data-testid="nav-audit" className={({ isActive }) => `side-link pl-6 pr-4 text-xs ${isActive ? "active" : ""}`}><ScrollText className="w-3.5 h-3.5 text-semantic-primary" />Spare Part Audit</NavLink>
            <NavLink to="/service/audit" data-testid="nav-service-audit" className={({ isActive }) => `side-link pl-6 pr-4 text-xs ${isActive ? "active" : ""}`}><ScrollText className="w-3.5 h-3.5 text-semantic-primary" />Service Audit</NavLink>
          </div> : null}

          {user?.role === "admin" ? (
            <>
              <NavLink
                to="/manage-users"
                data-testid="nav-manage-users"
                className={`side-link ${isManageUsersActive ? "parent-active" : ""}`}
              >
                <Users className="h-4 w-4" style={{ color: "var(--service-jakarta-selected-accent)" }} strokeWidth={2} />
                Manage Users
              </NavLink>
              <button
                type="button"
                onClick={() => setMasterDataOpen((open) => !open)}
                aria-expanded={masterDataOpen}
                data-testid="nav-master-data-toggle"
                className={`side-link w-full ${isMasterDataActive ? "parent-active" : ""}`}
              >
                <Database className="w-4 h-4" style={{ color: "var(--service-jakarta-selected-accent)" }} strokeWidth={2} />
                <span className="flex-1 text-left">Master Data</span>
                <ChevronDown
                  className={`w-3.5 h-3.5 transition-transform ${masterDataOpen ? "rotate-180" : ""}`}
                  style={{ color: "var(--service-jakarta-selected-accent)" }}
                  strokeWidth={2}
                />
              </button>
              {masterDataOpen ? (
                <div className="py-1 ml-5 border-l border-zinc-200" data-testid="nav-master-data-submenu">
                  {MASTER_DATA_NAV.map((item) => (
                    <NavLink
                      key={item.to}
                      to={item.to}
                      data-testid={item.testid}
                      className={({ isActive }) => `side-link pl-6 pr-4 text-xs ${isActive ? "active" : ""}`}
                    >
                      <item.icon className="w-3.5 h-3.5 text-semantic-primary" strokeWidth={2} />
                      {item.label}
                    </NavLink>
                  ))}
                </div>
              ) : null}
            </>
          ) : null}
        </nav>
      </aside>

      {/* Main */}
      <main className="ml-[248px] h-screen min-w-0 flex flex-col">
        <header
          className="h-20 shrink-0 bg-card border-b border-zinc-200 px-8 flex items-center z-50"
          data-testid="page-header"
        >
          <div className="flex w-full items-end justify-between">
            <div>
              <div className="text-[11px] tracking-[0.08em] uppercase text-zinc-500 mb-1" data-testid="application-eyebrow">
                • Accurate • Transparent • Traceable
              </div>
              <h1 className="text-2xl font-semibold tracking-tight text-zinc-900" data-testid="application-identity">
                FLOWPI
              </h1>
            </div>
            <div className="flex items-center gap-4">
              <button type="button" onClick={() => setMode("mobile")} aria-label="Switch to Mobile View" className="inline-flex h-9 items-center gap-1.5 rounded border border-zinc-200 px-2 text-xs text-zinc-700 hover:bg-accent" data-testid="desktop-mobile-switch">
                <MonitorCog className="h-4 w-4" />Mobile View
              </button>
              <div className="text-[11px] text-zinc-500 tabular-nums">
                {new Date().toLocaleDateString("en-GB", {
                  day: "2-digit",
                  month: "short",
                  year: "numeric",
                })}
              </div>

              {/* Profile avatar + dropdown */}
              <div className="relative" ref={profileRef}>
                <button
                  onClick={() => setProfileOpen((o) => !o)}
                  aria-label="Open profile menu"
                  aria-expanded={profileOpen}
                  data-testid="profile-avatar-btn"
                  className="profile-gradient-control w-9 h-9 rounded-full border border-zinc-200 flex items-center justify-center text-xs font-medium hover:border-primary transition-colors"
                >
                  {user?.name?.[0]?.toUpperCase() || "?"}
                </button>

                {profileOpen && (
                  <div className="absolute right-0 top-full mt-2 w-52 bg-white border border-zinc-200 rounded-lg shadow-brutal z-50">
                    <div className="px-4 py-3">
                      <div className="text-sm font-medium text-zinc-900 truncate" data-testid="user-name">
                        {user?.name}
                      </div>
                      <div className="text-xs text-zinc-500 truncate mt-0.5">{user?.email}</div>
                    </div>
                    <div className="border-t border-zinc-200" />
                    <div className="py-1">
                      <button
                        onClick={() => { setProfileOpen(false); navigate("/profile"); }}
                        className="w-full flex items-center gap-2.5 px-4 py-2 text-xs text-zinc-700 hover:bg-accent transition-colors"
                      >
                        <Settings className="w-3.5 h-3.5" strokeWidth={2} />
                        Settings
                      </button>
                      <button
                        onClick={() => { setProfileOpen(false); logout(); }}
                        data-testid="logout-btn"
                        className="w-full flex items-center gap-2.5 px-4 py-2 text-xs text-zinc-700 hover:bg-accent hover:text-destructive transition-colors"
                      >
                        <LogOut className="w-3.5 h-3.5" strokeWidth={2} />
                        Sign out
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </header>
        <div
          className={`${contentPadding} flex-1 min-h-0 overflow-y-auto`}
          data-testid="main-scroll-container"
        >
          {backendUnavailable ? (
            <BackendUnavailableNotice retrying={retryingBackend} onRetry={handleBackendRetry} />
          ) : null}
          <FeatureErrorBoundary resetKey={location.pathname}>
            <Outlet />
          </FeatureErrorBoundary>
        </div>
      </main>
    </div>
  );
}
